// Резолвър от id-та към човешки имена за човешкия изглед на одит лога.
//
// Всички източници са вече заредени на клиента (React Query кеш):
//   - менюто (`useMenu`) дава продукти, станции, модификатори
//   - начините на плащане (`usePaymentMethods`)
// Затова човешкият изглед НЕ изисква никаква промяна по базата или бекенда —
// само превежда вече записаните в одита id-та.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMenu } from "../../../../hooks/useMenu";
import { usePaymentMethods } from "../../../../hooks/usePaymentMethods";
import {
  useProducts,
  useWarehouses,
  useSuppliers,
  useRecipes,
} from "../../../../hooks/useInventory";

export interface AuditLookups {
  /** Име на продукт по productId / item_id. Fallback: "Продукт #id". */
  itemName: (id: unknown) => string;
  /** Име на станция по stationId, или null ако липсва. */
  stationName: (id: unknown) => string | null;
  /** Име на модификатор по id. Fallback: "#id". */
  modifierName: (id: unknown) => string;
  /** Име на начин на плащане по id. Fallback: "Метод #id". */
  paymentMethodName: (id: unknown) => string;
  /** Име на складов продукт (`warehouse_products`), не на продаваемото. */
  warehouseProductName: (id: unknown) => string;
  /** Мерна единица на складов продукт, или null. */
  warehouseProductUnit: (id: unknown) => string | null;
  /** Име на склад по warehouseId, или null. */
  warehouseName: (id: unknown) => string | null;
  /** Име на доставчик по supplierId, или null. */
  supplierName: (id: unknown) => string | null;
  /** Име на рецепта по recipeId. Fallback: "Рецепта #id". */
  recipeName: (id: unknown) => string;
  /** Текущ език (bg/en) — за избор на превод. */
  lang: string;
  /** true когато менюто е заредено (иначе показваме fallback-и). */
  ready: boolean;
}

function asId(id: unknown): number | null {
  if (id === null || id === undefined || id === "") return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param withInventory зареди и складовите справочници (продукти, складове,
 *   доставчици, рецепти). Подава се `true` само когато отвореният запис е по
 *   `/inventory/*` — иначе одит страницата би дърпала четири списъка напразно.
 */
export function useAuditLookups(withInventory = false): AuditLookups {
  const { i18n, t } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "bg";
  const { data: menu } = useMenu();
  const { data: paymentMethods } = usePaymentMethods();
  const { data: warehouseProducts } = useProducts(withInventory);
  const { data: warehouses } = useWarehouses(withInventory);
  const { data: suppliers } = useSuppliers(withInventory);
  const { data: recipes } = useRecipes(withInventory);

  return useMemo(() => {
    const items = menu?.items ?? [];
    const stations = menu?.stations ?? [];
    const modifiers = menu?.modifiers ?? [];
    const methods = paymentMethods ?? [];

    const itemById = new Map(items.map((i) => [i.id, i]));
    const stationById = new Map(stations.map((s) => [s.id, s]));
    const modifierById = new Map(modifiers.map((m) => [m.id, m]));
    const methodById = new Map(methods.map((m) => [m.id, m]));

    const itemName = (raw: unknown): string => {
      const id = asId(raw);
      if (id === null) return t("auditLogDetail.human.unknownProduct");
      const item = itemById.get(id);
      if (!item) return `${t("auditLogDetail.human.product")} #${id}`;
      const lang_ = item.itemLangs.find((l) => l.languageCode === lang);
      return lang_?.name || item.itemLangs[0]?.name || item.slug || `#${id}`;
    };

    const stationName = (raw: unknown): string | null => {
      const id = asId(raw);
      if (id === null) return null;
      const station = stationById.get(id);
      if (!station) return null;
      const tr = station.translations.find((x) => x.languageCode === lang);
      return tr?.name || station.translations[0]?.name || station.slug || null;
    };

    const modifierName = (raw: unknown): string => {
      const id = asId(raw);
      if (id === null) return "—";
      const mod = modifierById.get(id);
      if (!mod) return `#${id}`;
      const tr = mod.translations.find((x) => x.languageCode === lang);
      return tr?.name || mod.translations[0]?.name || mod.slug || `#${id}`;
    };

    const paymentMethodName = (raw: unknown): string => {
      const id = asId(raw);
      if (id === null) return "—";
      const m = methodById.get(id);
      if (!m) return `${t("auditLogDetail.human.method")} #${id}`;
      return (lang === "en" ? m.name_en : m.name_bg) || m.name_bg || `#${id}`;
    };

    // ── Складови справочници ────────────────────────────────────────────────
    // Имената тук са плоски низове (без превод) — складът е едноезичен модул.
    const warehouseProductById = new Map(
      (warehouseProducts ?? []).map((p) => [p.id, p]),
    );
    const warehouseById = new Map((warehouses ?? []).map((w) => [w.id, w]));
    const supplierById = new Map((suppliers ?? []).map((s) => [s.id, s]));
    const recipeById = new Map((recipes ?? []).map((r) => [r.id, r]));

    const warehouseProductName = (raw: unknown): string => {
      const id = asId(raw);
      if (id === null) return t("auditLogDetail.human.unknownProduct");
      const product = warehouseProductById.get(id);
      return product?.name || `${t("auditLogDetail.human.product")} #${id}`;
    };

    const warehouseProductUnit = (raw: unknown): string | null => {
      const id = asId(raw);
      if (id === null) return null;
      return warehouseProductById.get(id)?.unit || null;
    };

    const warehouseName = (raw: unknown): string | null => {
      const id = asId(raw);
      if (id === null) return null;
      return warehouseById.get(id)?.name || `#${id}`;
    };

    const supplierName = (raw: unknown): string | null => {
      const id = asId(raw);
      if (id === null) return null;
      return supplierById.get(id)?.name || `#${id}`;
    };

    const recipeName = (raw: unknown): string => {
      const id = asId(raw);
      if (id === null) return t("auditLogDetail.human.unknownProduct");
      return recipeById.get(id)?.name || `${t("auditLogDetail.human.recipe")} #${id}`;
    };

    return {
      itemName,
      stationName,
      modifierName,
      paymentMethodName,
      warehouseProductName,
      warehouseProductUnit,
      warehouseName,
      supplierName,
      recipeName,
      lang,
      ready: items.length > 0,
    };
  }, [
    menu,
    paymentMethods,
    warehouseProducts,
    warehouses,
    suppliers,
    recipes,
    lang,
    t,
  ]);
}
